import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Bell, Bookmark, Bot, CreditCard, Hash, Laptop, MessageSquare, Search, Settings, ShieldCheck, Sun, Moon } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { useTheme } from "../theme";
import { Button } from "../ui/button";
import { AdminDialog } from "./AdminDialog";
import { AccountSecurityDialog } from "./AccountSecurityDialog";
import { Conversation } from "./Conversation";
import { NotificationSettingsDialog } from "./NotificationSettingsDialog";
import { BrowseChannelsDialog, CreateChannelDialog, NewDirectMessageDialog } from "./dialogs";
import { readHashConversation, writeHashConversation } from "./format";
import { SearchPalette } from "./SearchPalette";
import { SavedMessagesDialog } from "./SavedMessagesDialog";
import { Sidebar } from "./Sidebar";
import { isAdmin, type MeReady } from "./types";
import { monitorSessionActivity } from "./sessionActivity";

import { usePhoneViewport } from "./usePhoneViewport";

type DialogKind = null | "create-channel" | "browse-channels" | "new-dm";

export function Workspace({ me }: { me: MeReady }) {
  usePhoneViewport();
  const conversations = useQuery(api.communication.listConversations, {});
  const notificationStatus = useQuery(api.notifications.status, {});
  const sessionTrust = useQuery(api.passkeyStore.currentSessionTrust, {});
  const selectWorkspace = useMutation(api.workspace.select);
  const trustCurrentSession = useMutation(api.passkeyStore.trustCurrentSession);
  const lockCurrentSession = useMutation(api.passkeyStore.lockCurrentSession);
  const { theme, setTheme } = useTheme();
  const [selectedId, setSelectedId] = useState<string | null>(() => readHashConversation());
  const [threadRootId, setThreadRootId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountSecurityOpen, setAccountSecurityOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState<string | null>(null);
  const [workspaceSwitchError, setWorkspaceSwitchError] = useState<string | null>(null);
  const [trustBusy, setTrustBusy] = useState(false);
  const [trustError, setTrustError] = useState<string | null>(null);
  // Keep the web client compatible while the production backend is still on
  // the original single-workspace response shape.
  const compatibleMe = me as Omit<MeReady, "workspaces"> & { workspaces?: MeReady["workspaces"] };
  const workspaces = compatibleMe.workspaces ?? [{ id: me.workspace.id, name: me.workspace.name, slug: me.workspace.slug, role: me.role }];

  const selected = useMemo(() => conversations?.find((conversation) => conversation.id === selectedId) ?? null, [conversations, selectedId]);

  const openConversation = useCallback((id: string, thread: string | null = null) => {
    setSelectedId(id);
    setThreadRootId(thread);
    setSidebarOpen(false);
    setSearchOpen(false);
    setDialog(null);
    writeHashConversation(id);
  }, []);

  // Follow the URL hash (back/forward, deep links) and pick #general on first load.
  useEffect(() => {
    const onHash = () => { setSelectedId(readHashConversation()); setThreadRootId(null); };
    window.addEventListener("hashchange", onHash);
    return () => { window.removeEventListener("hashchange", onHash); };
  }, []);
  useEffect(() => {
    if (conversations === undefined || conversations.length === 0) return;
    if (selectedId !== null && conversations.some((conversation) => conversation.id === selectedId)) return;
    const general = conversations.find((conversation) => conversation.name === "general") ?? conversations[0];
    if (general !== undefined) { setSelectedId(general.id); writeHashConversation(general.id); }
  }, [conversations, selectedId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setSearchOpen(true); return; }
      if (event.key !== "Escape") return;
      if (searchOpen) setSearchOpen(false);
      else if (dialog !== null) setDialog(null);
      else if (adminOpen) setAdminOpen(false);
      else if (notificationsOpen) setNotificationsOpen(false);
      else if (accountSecurityOpen) setAccountSecurityOpen(false);
      else if (savedOpen) setSavedOpen(false);
      else if (threadRootId !== null) setThreadRootId(null);
      else if (sidebarOpen) setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [searchOpen, dialog, adminOpen, notificationsOpen, accountSecurityOpen, savedOpen, threadRootId, sidebarOpen]);

  const heartbeatPresence = useMutation(api.communication.heartbeatPresence);
  useEffect(() => {
    const send = () => { void heartbeatPresence({ state: document.visibilityState === "hidden" ? "away" : "online" }); };
    const sendOffline = () => { void heartbeatPresence({ state: "offline" }); };
    send();
    const timer = window.setInterval(send, 45_000);
    document.addEventListener("visibilitychange", send);
    window.addEventListener("beforeunload", sendOffline);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", send);
      window.removeEventListener("beforeunload", sendOffline);
    };
  }, [heartbeatPresence]);

  const activityKey = sessionTrust?.activityKey;
  const trusted = sessionTrust?.trusted;
  useEffect(() => {
    if (trusted !== false || !activityKey) return;
    return monitorSessionActivity({ target: window, activityKey, timeoutMs: me.workspace.inactivityLockMinutes * 60_000, onIdle: () => lockCurrentSession({}) });
  }, [me.workspace.inactivityLockMinutes, lockCurrentSession, trusted, activityKey]);

  const trustComputer = async () => {
    setTrustBusy(true); setTrustError(null);
    try { await trustCurrentSession({}); }
    catch { setTrustError("We couldn’t trust this computer. Unlock Yodl and try again."); }
    finally { setTrustBusy(false); }
  };

  const billing = me.billing ?? null;
  const trialDaysLeft = billing?.trialEndsAt !== undefined ? Math.max(0, Math.ceil((billing.trialEndsAt - Date.now()) / 86_400_000)) : null;
  const trialEndsOn = billing?.trialEndsAt !== undefined ? new Date(billing.trialEndsAt).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : null;
  const cycleTheme = () => { setTheme(theme === "dark" ? "light" : "dark"); };
  const ThemeIcon = theme === "dark" ? Moon : Sun;
  const totalUnread = (conversations ?? []).reduce((sum, conversation) => sum + conversation.unreadCount, 0);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    if (workspaceId === me.workspace.id || switchingWorkspaceId !== null) return;
    setSwitchingWorkspaceId(workspaceId);
    setWorkspaceSwitchError(null);
    try {
      await selectWorkspace({ workspaceId });
    } catch {
      setWorkspaceSwitchError("We couldn’t switch workspaces. Please try again.");
    } finally {
      setSwitchingWorkspaceId(null);
    }
  }, [me.workspace.id, selectWorkspace, switchingWorkspaceId]);

  useEffect(() => {
    document.title = totalUnread > 0 ? `(${totalUnread}) ${me.workspace.name} · Yodl` : `${me.workspace.name} · Yodl`;
  }, [totalUnread, me.workspace.name]);

  return (
    <div className="app-shell">
      {workspaceSwitchError !== null ? <p className="workspace-switch-error" role="alert">{workspaceSwitchError}</p> : null}
      {sidebarOpen ? <button className="mobile-backdrop" type="button" aria-label="Close sidebar" onClick={() => { setSidebarOpen(false); }} /> : null}
      <nav className="workspace-rail" aria-label="Workspace tools">
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
        <div className="workspace-switcher">
          {workspaces.map((workspace) => {
            const selectedWorkspace = workspace.id === me.workspace.id;
            const initials = workspace.name.split(/\s+/u).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase() ?? "").join("");
            return (
              <button
                key={workspace.id}
                type="button"
                className={`workspace-button${selectedWorkspace ? " workspace-button--selected" : ""}`}
                aria-label={selectedWorkspace ? `Current workspace: ${workspace.name}` : `Switch to ${workspace.name}`}
                aria-pressed={selectedWorkspace}
                title={workspace.name}
                disabled={switchingWorkspaceId !== null}
                onClick={() => { void switchWorkspace(workspace.id); }}
              >
                {initials || workspace.name.slice(0, 1).toUpperCase()}
              </button>
            );
          })}
        </div>
        <div className="rail-bottom">
          <Button size="icon" variant="ghost" aria-label="Search (⌘K)" title="Search (⌘K)" onClick={() => { setSearchOpen(true); }}><Search size={17} /></Button>
          <Button size="icon" variant="ghost" aria-label="Notifications" title="Notifications" onClick={() => { setNotificationsOpen(true); }}><Bell size={17} /></Button>
          <Button size="icon" variant="ghost" aria-label="Saved messages" title="Saved messages" onClick={() => { setSavedOpen(true); }}><Bookmark size={17} /></Button>
          <Button size="icon" variant="ghost" aria-label="Account security" title="Touch ID and passkeys" onClick={() => { setAccountSecurityOpen(true); }}><ShieldCheck size={17} /></Button>
          <Button size="icon" variant="ghost" aria-label={`Theme: ${theme}. Change theme.`} title={`Theme: ${theme}`} onClick={cycleTheme}><ThemeIcon size={17} /></Button>
          {isAdmin(me.role) ? <Button size="icon" variant="ghost" aria-label="Workspace settings" title="Members, invitations, agents" onClick={() => { setAdminOpen(true); }}><Settings size={17} /></Button> : null}
        </div>
      </nav>

      <Sidebar
        me={me}
        conversations={conversations ?? []}
        selectedId={selectedId}
        open={sidebarOpen}
        onSelect={openConversation}
        onSearch={() => { setSearchOpen(true); }}
        onCreateChannel={() => { setDialog("create-channel"); }}
        onBrowseChannels={() => { setDialog("browse-channels"); }}
        onNewDirectMessage={() => { setDialog("new-dm"); }}
        onOpenAdmin={() => { setAdminOpen(true); }}
        workspaces={workspaces}
        switchingWorkspaceId={switchingWorkspaceId}
        onSwitchWorkspace={(workspaceId) => { void switchWorkspace(workspaceId); }}
        theme={theme}
        onCycleTheme={cycleTheme}
      />

      <main className="main-area" id="main-content">
      {sessionTrust !== undefined && !sessionTrust.trusted ? (
        <div className="trusted-computer-banner">
          <Laptop size={17} />
          <span>
            <strong>Stop repeated Yodl sign-ins</strong>
            {` Stay signed in for 30 days without repeated passkey checks. Use this only on your own password-protected computer. Shared computers still lock after ${me.workspace.inactivityLockMinutes} unattended minutes.`}
          </span>
          <Button size="compact" disabled={trustBusy} onClick={() => { void trustComputer(); }}>{trustBusy ? "Saving…" : "Trust this computer for 30 days"}</Button>
        </div>
      ) : null}
      {trustError !== null ? <p className="workspace-switch-error" role="alert">{trustError}</p> : null}
      {billing?.trial === true ? (
        <div className="trial-banner" role="status">
          <CreditCard size={17} />
          <span>
            <strong>Free trial{trialDaysLeft !== null ? `: ${String(trialDaysLeft)} day${trialDaysLeft === 1 ? "" : "s"} left` : ""}.</strong>
            {` Your card is charged on ${trialEndsOn ?? "the trial end date"} unless you cancel first. Protected health information stays off until the first payment and your signed BAA.`}
          </span>
        </div>
      ) : billing?.pastDue === true ? (
        <div className="trial-banner trial-banner--warning" role="alert">
          <CreditCard size={17} />
          <span><strong>Payment did not go through.</strong> Stripe is retrying the card on file. Update it from the Stripe email, or write to info@phiclaw.ai, before Yodl locks this workspace.</span>
        </div>
      ) : null}
      {notificationStatus !== undefined && !notificationStatus.enabled ? <div className="notification-enrollment"><Bell size={16} /><span><strong>Turn on private notifications</strong> so you don’t miss staff messages when Yodl is closed.</span><Button size="compact" onClick={() => { setNotificationsOpen(true); }}>Enable</Button></div> : null}
        {selected !== null ? (
          <Conversation
            key={selected.id}
            me={me}
            conversation={selected}
            threadRootId={threadRootId}
            onOpenThread={setThreadRootId}
            onOpenSidebar={() => { setSidebarOpen(true); }}
            onOpenNotifications={() => { setNotificationsOpen(true); }}
            onOpenConversation={openConversation}
          />
        ) : (
          <div className="empty-state">
            {conversations === undefined ? <p>Loading…</p> : (
              <>
                <div className="channel-intro__icon"><MessageSquare size={20} /></div>
                <h2>Pick a conversation</h2>
                <p>Choose a channel on the left, or start a direct message.</p>
                <div className="empty-state__actions">
                  <Button onClick={() => { setDialog("browse-channels"); }}><Hash size={14} /> Browse channels</Button>
                  <Button onClick={() => { setDialog("new-dm"); }}><Bot size={14} /> New message</Button>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {searchOpen ? <SearchPalette conversations={conversations ?? []} onClose={() => { setSearchOpen(false); }} onOpen={openConversation} /> : null}
      {adminOpen ? <AdminDialog me={me} onClose={() => { setAdminOpen(false); }} /> : null}
      {notificationsOpen ? <NotificationSettingsDialog onClose={() => { setNotificationsOpen(false); }} /> : null}
      {accountSecurityOpen ? <AccountSecurityDialog onClose={() => { setAccountSecurityOpen(false); }} /> : null}
      {savedOpen ? <SavedMessagesDialog onClose={() => { setSavedOpen(false); }} onOpen={(conversationId) => { openConversation(conversationId); }} /> : null}
      {dialog === "create-channel" ? <CreateChannelDialog me={me} onClose={() => { setDialog(null); }} onCreated={openConversation} /> : null}
      {dialog === "browse-channels" ? <BrowseChannelsDialog onClose={() => { setDialog(null); }} onJoined={openConversation} /> : null}
      {dialog === "new-dm" ? <NewDirectMessageDialog me={me} onClose={() => { setDialog(null); }} onCreated={openConversation} /> : null}
    </div>
  );
}
